import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import Breadcrumb from '../components/Breadcrumb';
import { ArrowLeft, Loader2, ExternalLink } from 'lucide-react';
import type { Product } from '../types/product';
import { useAuth } from '../hooks/useAuth';
import EmailVerificationModal from '../components/modals/EmailVerificationModal';

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { setItems } = useBreadcrumb();

  React.useEffect(() => {
    async function fetchProduct() {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('products')
          .select(`
            *,
            category:product_categories(name)
          `)
          .eq('slug', slug)
          .single();

        if (error) throw error;
        setProduct(data);

        // Set breadcrumb
        setItems([
          { label: 'Home', path: '/' },
          { label: 'Shop', path: '/shop' },
          { label: data.name, path: `/shop/${data.slug}` }
        ]);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      fetchProduct();
    }
  }, [slug, setItems]);

  const handleDownload = async () => {
    if (!product?.external_url) return;

    // If product is free and user is not logged in
    if (product.price === 0 && !user) {
      const storedEmail = localStorage.getItem('blooma_email');
      
      if (storedEmail) {
        try {
          // Check if user exists in our database
          const { data: existingUser } = await supabase
            .from('unauth_users')
            .select('status')
            .eq('email', storedEmail.toLowerCase())
            .maybeSingle();

          // If user exists and is verified, allow download
          if (existingUser?.status === 'verified') {
            window.location.href = product.external_url;
            return;
          }
        } catch (err) {
          console.error('Error checking user:', err);
          // Continue to email modal if there's an error
        }
      }
      
      // Show email modal if no stored email or user isn't verified
      setShowEmailModal(true);
      return;
    }

    // Open download directly for logged in users or paid products
    window.location.href = product.external_url;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-red-600">
          {error || 'Product not found'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Product Image */}
          <div className="aspect-square bg-white rounded-lg overflow-hidden">
            <img 
              src={product.image_url} 
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              {product.category && (
                <p className="text-primary-600 font-medium mb-2">
                  {product.category.name}
                </p>
              )}
              <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
              <p className="text-gray-600">{product.description}</p>
            </div>

            <div className="border-t border-b py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-gray-900">
                    {product.price === 0 ? 'Free' : `$${product.price.toFixed(2)}`}
                  </p>
                </div>
                {product.external_url && (
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    <span>{product.price === 0 ? 'Download Now' : 'Buy Now'}</span>
                    <ExternalLink className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={() => navigate('/shop')}
              className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Shop</span>
            </button>
          </div>
        </div>
      </div>

      <EmailVerificationModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onVerified={() => {
          setShowEmailModal(false);
          if (product.external_url) {
            window.location.href = product.external_url;
          }
        }}
      />
    </div>
  );
}